import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonBase, Chip, Skeleton, Typography } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { HelpNewsItem, KLINVIA_BLOG_URL, listHelpNews } from '../../../services/assistantCentral';
import AssistantMarkdown from '../assistant/AssistantMarkdown';
import MessengerHeader from './MessengerHeader';
import {
  MESSENGER,
  formatNewsDate,
  messengerCardVerticalButtonSx,
  messengerSubtitleSx,
  messengerTitleSx,
} from './messengerTheme';

export function NewsMeta({ item }: { item: HelpNewsItem }) {
  const parts = [formatNewsDate(item.date), item.read_minutes ? `${item.read_minutes} min de leitura` : ''].filter(Boolean);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Chip size="small" label={item.tag} sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: MESSENGER.tealSoft, color: MESSENGER.tealDark }} />
      <Typography sx={{ fontSize: '0.72rem', color: MESSENGER.slateFaint }}>{parts.join(' · ')}</Typography>
    </Box>
  );
}

export function NewsCover({ item, height }: { item: HelpNewsItem; height: number }) {
  if (!item.cover_url) return null;
  return (
    <Box
      component="img"
      src={item.cover_url}
      alt=""
      loading="lazy"
      sx={{ display: 'block', width: '100%', height, objectFit: 'cover', bgcolor: MESSENGER.inputBg }}
    />
  );
}

function openExternal(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

interface MessengerNewsProps {
  onClose: () => void;
  onOpenItem: (item: HelpNewsItem) => void;
}

export const MessengerNews: React.FC<MessengerNewsProps> = ({ onClose, onOpenItem }) => {
  const [items, setItems] = useState<HelpNewsItem[] | null>(null);

  useEffect(() => {
    let alive = true;
    listHelpNews()
      .then((data) => alive && setItems(data))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: MESSENGER.canvas }}>
      <MessengerHeader title="Novidades" onClose={onClose} />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1.5,
        }}
      >
        {items === null && [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={220} sx={{ flexShrink: 0 }} />)}
        {items?.length === 0 && (
          <Box sx={{ textAlign: 'center', pt: 8, color: MESSENGER.slate }}>
            <CampaignOutlinedIcon sx={{ fontSize: 40, color: MESSENGER.slateFaint, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: MESSENGER.navy, fontSize: '0.92rem' }}>Sem novidades por enquanto</Typography>
          </Box>
        )}
        {items?.map((item) => (
          <ButtonBase key={item.id} onClick={() => onOpenItem(item)} sx={messengerCardVerticalButtonSx}>
            <NewsCover item={item} height={150} />
            <Box sx={{ px: 2, py: 1.6 }}>
              <NewsMeta item={item} />
              <Typography sx={{ ...messengerTitleSx, mt: 0.75 }}>{item.title}</Typography>
              <Typography
                sx={{ ...messengerSubtitleSx, mt: 0.35, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {item.summary}
              </Typography>
            </Box>
          </ButtonBase>
        ))}
        {items && items.some((i) => i.url) && (
          <Button
            endIcon={<OpenInNewIcon />}
            onClick={() => openExternal(KLINVIA_BLOG_URL)}
            sx={{ alignSelf: 'center', flexShrink: 0, textTransform: 'none', fontWeight: 700, color: MESSENGER.tealDark }}
          >
            Ver todos no blog Klinvia
          </Button>
        )}
      </Box>
    </Box>
  );
};

interface MessengerNewsItemProps {
  item: HelpNewsItem;
  onBack: () => void;
  onClose: () => void;
  onNavigate: (href: string) => void;
}

export const MessengerNewsItem: React.FC<MessengerNewsItemProps> = ({ item, onBack, onClose, onNavigate }) => (
  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: MESSENGER.surface }}>
    <MessengerHeader title="Novidades" onBack={onBack} onClose={onClose} />
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <NewsCover item={item} height={190} />
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <NewsMeta item={item} />
        <Typography component="h1" sx={{ mt: 1, fontWeight: 800, fontSize: '1.3rem', lineHeight: 1.25, color: MESSENGER.navy, letterSpacing: '-0.015em' }}>
          {item.title}
        </Typography>
        <Typography sx={{ ...messengerSubtitleSx, fontSize: '0.875rem', lineHeight: 1.6, mt: 1, mb: 2 }}>{item.summary}</Typography>
        {item.body && (
          <AssistantMarkdown content={item.body} onNavigate={onNavigate} sx={{ fontSize: '0.875rem', lineHeight: 1.65, color: MESSENGER.navy }} />
        )}
        {item.url && (
          <Button
            variant="contained"
            disableElevation
            endIcon={<OpenInNewIcon />}
            onClick={() => openExternal(item.url as string)}
            sx={{ mt: 1, textTransform: 'none', fontWeight: 700, borderRadius: '10px', bgcolor: MESSENGER.teal, '&:hover': { bgcolor: MESSENGER.tealDark } }}
          >
            Ler artigo completo
          </Button>
        )}
        {item.href && (
          <Button
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            onClick={() => onNavigate(item.href as string)}
            sx={{ mt: 2.5, textTransform: 'none', fontWeight: 700, borderRadius: '10px', borderColor: MESSENGER.teal, color: MESSENGER.tealDark }}
          >
            Abrir no sistema
          </Button>
        )}
      </Box>
    </Box>
  </Box>
);
